import type { ICustomField, CustomFieldType, ITrigger, IFlow, IWarehouse } from '@nova/core';
import type { Ref, ComputedRef, Component, MaybeRef } from 'vue';

/**
 * What CustomFieldTimestamp emits: an ISO 8601 string carrying the warehouse's offset, or the
 * split controls when `returnObject` is set. Exported so a parent binding v-model can type both
 * sides of the round trip with the same shape the component actually produces.
 *
 * A string rather than a Luxon DateTime because `isValidCustomField` validates a timestamp with
 * `isDateString(value)`, which an object fails, and because the value is posted as-is — the wire
 * format belongs here explicitly rather than falling out of Luxon's `toJSON()`.
 */
export type TimestampValue = string | null | { date: string; time: string; amPm: string };

/** Form data entry as returned from getCustomFormsData (with label/value formatted) */
export interface CustomFormDataEntry {
  id?: string;
  label?: string;
  value?: unknown;
  formField?: { id: string; field?: { type: CustomFieldType }; overrideLabel?: string };
  [key: string]: unknown;
}

/** Form field shape used by getCustomField (flow formField with nested field) */
export interface FormFieldLike {
  id: string;
  required?: boolean;
  overrideLabel?: string | null;
  overrideDescription?: string | null;
  order?: number;
  field: {
    type: CustomFieldType;
    label?: string;
    description?: string;
    placeholder?: string;
    extraFields?: {
      dropDownValues?: unknown;
      minLengthOrValue?: number | null;
      maxLengthOrValue?: number | null;
    };
  };
}

/**
 * ADR-4: Unified Props Interface for Custom Field Components
 *
 * All custom field components accept these props. Apps can use different
 * styling variants (outlined, singleLine) to match their design patterns.
 */
export interface CustomFieldProps {
  // Required
  field: ICustomField;

  // Display options (all optional with defaults)
  fieldIcon?: string;
  displayLabel?: boolean;
  hideDetails?: boolean | 'auto';

  // Styling variants (support both Helios and Comet patterns)
  /**
   * @deprecated Unread by any field type — Design fixes density/variant/color for every field
   * type; kept only for backward compatibility.
   */
  outlined?: boolean;
  /** @deprecated Unread by any field type; kept only for backward compatibility. */
  singleLine?: boolean;
  persistentPlaceholder?: boolean;
  /** @deprecated No longer read — Design fixes density to "comfortable" for every field type. */
  density?: string;
  /** @deprecated No longer read — Design fixes variant to "outlined" for every field type. */
  variant?: string;
  /** @deprecated No longer read — Design fixes color to "primary" for every field type. */
  color?: string;

  // Context (optional, can be injected if not provided)
  timezone?: string;
  militaryTimeEnabled?: boolean;

  /** Read by CustomFieldPhone.vue to pick the phone input's default country. */
  warehouse?: IWarehouse;

  // Timestamp only
  /** Hides the timezone label under the time input. */
  hideTimezone?: boolean;
  /** Emit `{ date, time, amPm }` instead of an ISO string. */
  returnObject?: boolean;
  /** Defaults to `isMobile()` when not passed. */
  compact?: boolean;

  /**
   * The field's data-testid, built by the caller with `getCustomFieldTestId` from `@nova/core`.
   * Every type puts it on its control, the element a test fills or clicks; a timestamp, which has
   * two, puts it on their group and `<id>-date-picker` / `<id>-time-input` on each. Without it,
   * no id is set.
   */
  testId?: string;

  // Standard props
  modelValue?: string | boolean | number | string[] | null;
  required?: boolean;
  readOnly?: boolean;
  rules?: Array<(value: any) => boolean | string>;
}

/**
 * Options for useCustomForms composable
 */
export interface UseCustomFormsOptions {
  timezone?: MaybeRef<string | undefined>;
  militaryTimeEnabled?: MaybeRef<boolean | undefined>;
  /** Triggers list (can be a ref/computed for reactivity; unref'd — not a getter) */
  triggers?: MaybeRef<ITrigger[] | undefined>;
  warehouseId?: string;
  /** Called when a success/warning message should be shown (e.g. "Appointment data has been updated") */
  onNotify?: (message: string, type: 'success' | 'warning' | 'error') => void;
  /** Called when custom form data has been updated (e.g. to emit event or refresh) */
  onCustomFormsDataChange?: () => void;
}

/**
 * Return type for useCustomForms composable
 */
export interface UseCustomFormsReturn {
  activeTrigger: Ref<ITrigger | null>;
  /** Active flow from the current trigger */
  activeFlow: ComputedRef<IFlow | null>;
  /** Form data keyed by triggerId */
  customFormsData: Ref<Record<string, CustomFormDataEntry[]>>;
  pendingStatusChange: Ref<unknown>;
  hasActiveTrigger: ComputedRef<boolean>;
  hasCustomFormsData: ComputedRef<boolean>;
  /** Map CustomFieldType -> kebab component name (e.g. CustomFieldString -> 'custom-field-string') */
  customFieldComponents: ComputedRef<Record<string, string>>;
  /** Map CustomFieldType -> { component } for dynamic rendering */
  customFormsComponentsMapping: ComputedRef<Record<string, { component: Component }>>;
  getCustomField: (formField: FormFieldLike) => ICustomField;
  getFormattedValue: (field: CustomFormDataEntry) => string;
  createBulkCustomFormsData: (value: CustomFormDataEntry[], warehouseId: string) => Promise<void>;
  updateCustomFormsData: (value: CustomFormDataEntry[]) => Promise<void>;
  handleActionFieldTriggered: (actionName: string) => void;
  customFormsDataChange: () => void;
  cleanUpCustomFormsData: (triggerId?: string) => void;
  checkForTriggerOnStatus: (
    status: string,
    triggers: ITrigger[] | undefined
  ) => ITrigger | undefined;
  getCustomFormsData: (
    objectId: string,
    triggerId: string,
    lastChangedDateTimeOnly?: boolean
  ) => Promise<void | string>;
  getAppointmentRelatedAssetContainers: (appointmentId: string) => Promise<unknown[]>;
}

/** Object IDs used to fetch custom form data (appointment and/or asset visit) */
export interface CustomFormsDataObjectIds {
  appointmentId?: string;
  assetVisitId?: string;
}

/** Trigger shape required for custom forms data (id, dataEntityName) */
export interface CustomFormsDataTrigger {
  id: string;
  dataEntityName: string;
}

/**
 * Options for useCustomFormsData composable.
 * Equivalent to Helios customFormsDataMixin props; values can be refs for reactivity.
 */
export interface UseCustomFormsDataOptions {
  /** Object IDs (appointmentId and/or assetVisitId). Required; at least one key must be present. */
  objectIds: CustomFormsDataObjectIds | Ref<CustomFormsDataObjectIds>;
  /** Trigger with id and dataEntityName (e.g. appointment | assetvisit). */
  trigger: CustomFormsDataTrigger | Ref<CustomFormsDataTrigger>;
  /** Timezone for formatting timestamp fields (can be Ref for reactivity). */
  timezone: string | Ref<string>;
  /** Whether to format times in 24h (can be Ref for reactivity). */
  militaryTimeEnabled?: boolean | Ref<boolean>;
  /**
   * Optional Luxon date+time pattern from company display prefs.
   * When set, timestamps use this pattern (date order + clock) instead of MDY + military toggle.
   */
  dateTimeFormatPattern?: string | Ref<string | undefined>;
  /**
   * Optional Luxon date-only pattern from company display prefs.
   * When set, Date custom fields use this instead of hardcoded MM/dd/yyyy.
   */
  dateFormatPattern?: string | Ref<string | undefined>;
  /**
   * Active UI locale for weekday tokens (EEE) when formatting timestamps / last-updated.
   * Falls back to the formatter default (en) when omitted.
   */
  locale?: string | Ref<string | undefined>;
  /** Prefetched data to use instead of fetching (skips API call when set). */
  prefetchedCustomFormData?: unknown[] | null;
  /** Data to prepend to the fetched list (e.g. from another source). */
  prependedData?: CustomFormDataEntry[];
}

/**
 * Return type for useCustomFormsData composable.
 * Aligned with Helios customFormsDataMixin for migration (CustomFormsData.vue, Luna).
 */
export interface UseCustomFormsDataReturn {
  /** Submitted custom form data entries (with label/value formatted). */
  customFormData: Ref<CustomFormDataEntry[]>;
  loading: Ref<boolean>;
  error: Ref<Error | null>;
  /** True when customFormData has at least one entry. */
  hasCustomFormData: ComputedRef<boolean>;
  /** Fetches and processes custom form data; uses objectIds and trigger from options. */
  getCustomFormData: () => Promise<void>;
  /** Process raw API data (prepend + label/value formatting). Used for prefetched data. */
  processCustomFormData: (data: unknown[] | null | undefined) => CustomFormDataEntry[];
  /** Format a single field value for display (Document/MultiDocument links, Timestamp with timezone). */
  formattedValue: (field: CustomFormDataEntry) => string;
  /** Format a date for display (e.g. "Last updated at ..."). */
  getFormattedDate: (date: string | Date) => string;
}

/**
 * Storage context for document/multi-document custom form fields.
 * Apps provide this via CUSTOM_FORMS_STORAGE_CONTEXT_KEY so that
 * CustomFieldDocument and CustomFieldMultidocument can upload files.
 * Public endpoints use {checkin|checkout}/public/:warehouseId/storage.
 */
export interface CustomFormsStorageContext {
  warehouseId: string;
  storageEndpoint: string;
}

/**
 * Options for useStorageConfig composable
 */
export interface UseStorageConfigOptions {
  warehouseId?: string;
  s3BaseUrl?: string;
}

/**
 * Return type for useStorageConfig composable
 */
export interface UseStorageConfigReturn {
  s3BaseUrl: Ref<string>;
  getDownloadUrl: (fileKey: string) => string;
}

/**
 * Response shape from storage API (Neutron StorageDto).
 */
export interface StorageResponse {
  key: string;
  url: string;
}

/**
 * Error shape from HTTP client (e.g. Axios) for extracting API error message.
 */
export interface HttpErrorWithMessage {
  response?: { data?: { message?: string } };
}

/**
 * Single failed file upload with error message.
 */
export interface FailedUpload {
  file: File;
  error: string;
}

/**
 * Result of running uploads (Promise.allSettled): successful keys and failed items.
 */
export interface UploadResult {
  keys: string[];
  failed: FailedUpload[];
}

/**
 * Type guard for errors that may have response.data.message (e.g. Axios).
 */
export function isHttpErrorWithMessage(err: unknown): err is HttpErrorWithMessage {
  return Boolean(err && typeof err === 'object' && 'response' in err);
}

/**
 * Options for document upload components
 */
export interface DocumentUploadProps {
  // Required
  warehouseId: string;
  storageEndpoint: string;

  // Multiple file support
  multiple?: boolean;
  maxFiles?: number;

  // Storage configuration
  s3BaseUrl?: string;

  // Standard props
  modelValue?: string | string[];
  required?: boolean;
  maxSize?: number;
  conversionUnit?: 'KB' | 'MB';
  allowedMimeTypes?: string[];
  rules?: Array<(value: any) => boolean | string>;
  readOnly?: boolean;

  // UI customization
  /** @deprecated No longer affects rendering — Design fixes the underlying Vuetify variant to "outlined". */
  variant?: 'default' | 'simple';
  placeholder?: string;
  /** null renders the file input with no label; undefined falls back to the component default. */
  label?: string | null;
  description?: string;
}

/**
 * Props for DocumentUploadButton (extends DocumentUploadProps with optional overrides).
 */
export interface DocumentUploadButtonProps extends Partial<DocumentUploadProps> {
  warehouseId?: string;
  storageEndpoint?: string;
  modelValue?: string | string[];
}

/**
 * Emits for DocumentUploadButton.
 */
export interface DocumentUploadButtonEmits {
  'update:model-value': [value: string | string[]];
  error: [error: Error];
  uploading: [];
  uploaded: [];
  cleared: [];
}

/**
 * Re-export core types for convenience
 */
export type { ICustomField, CustomFieldType, ITrigger, IFlow };
